import { Lock, LockOpen, TextFields } from "@mui/icons-material";

import { Stack } from "@mui/material";

import type { EditorOptions } from "@tiptap/core";

import { JSONContent } from "@tiptap/react";

import { useCallback, useRef, useState } from "react";

import {
  LinkBubbleMenu,
  MenuButton,
  RichTextEditor,
  RichTextEditorRef,
  TableBubbleMenu,
  insertImages,
  useRichTextEditorContext,
} from "mui-tiptap";

import EditorMenuControls from "./EditorMenuControls";

import useExtensions from "./useExtensions";

function BubbleMenus() {
  const editorContext = useRichTextEditorContext();

  if (!editorContext || editorContext.isDestroyed) {
    return null;
  }

  const hasLinkHandler = editorContext.extensionManager.extensions.some(
    (e) => e.name === "linkBubbleMenuHandler",
  );

  if (!hasLinkHandler) {
    return null;
  }

  return (
    <>
      <LinkBubbleMenu />
      <TableBubbleMenu />
    </>
  );
}

function fileListToImageFiles(fileList: FileList): File[] {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();

    return mimeType.startsWith("image/");
  });
}

interface EditorProps {
  content?: JSONContent;

  onChange?: (json: JSONContent) => void;
}

export default function Editor({ content, onChange }: EditorProps) {
  const extensions = useExtensions({
    placeholder: "Escriba el contenido aquí...",
  });

  const rteRef = useRef<RichTextEditorRef>(null);

  const [isEditable, setIsEditable] = useState(true);

  const [showMenuBar, setShowMenuBar] = useState(true);
 //Descometnar handleNewImageFiles handleDrop handlePaste cuando se precise agregar imagenes en el editor de texto.
 //EN caso de querer permitir que se agreguen imagenes en el editor de texto se debe descomentar en este archivo.
 //Se debe descomentar tambien en useExtensions.ts y en EditorMenuControls.tsx
  /* const handleNewImageFiles = useCallback(
    (files: File[], insertPosition?: number): void => {
      if (!rteRef.current?.editor) {
        return;
      }

      const attributesForImageFiles = files.map((file) => ({
        src: URL.createObjectURL(file),
        alt: file.name,
      }));

      insertImages({
        images: attributesForImageFiles,

        editor: rteRef.current.editor,

        position: insertPosition,
      });
    },
    [],
  ); */

  /* const handleDrop: NonNullable<EditorOptions["editorProps"]["handleDrop"]> =
    useCallback(
      (view, event, _slice, _moved) => {
        if (!(event instanceof DragEvent) || !event.dataTransfer) {
          return false;
        }

        const imageFiles = fileListToImageFiles(event.dataTransfer.files);

        if (imageFiles.length > 0) {
          const insertPosition = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })?.pos;

          handleNewImageFiles(imageFiles, insertPosition);

          event.preventDefault();

          return true;
        }

        return false;
      },
      [handleNewImageFiles],
    ); */

 /*  const handlePaste: NonNullable<EditorOptions["editorProps"]["handlePaste"]> =
    useCallback(
      (_view, event, _slice) => {
        if (!event.clipboardData) {
          return false;
        }

        const pastedImageFiles = fileListToImageFiles(
          event.clipboardData.files,
        );

        if (pastedImageFiles.length > 0) {
          handleNewImageFiles(pastedImageFiles);

          return true;
        }

        return false;
      },
      [handleNewImageFiles],
    ); */

  return (
    <RichTextEditor
      ref={rteRef}
      extensions={extensions}
      immediatelyRender={false}
      content={content}
      editable={isEditable}
      //Descomentar cunado se precise agrgear imagenes en el editor de texto
      /* editorProps={{
        handleDrop,
        handlePaste,
      }} */
      onUpdate={({ editor }) => {
        onChange?.(editor.getJSON());
      }}
      renderControls={() => <EditorMenuControls />}
      RichTextFieldProps={{
        variant: "outlined",

        MenuBarProps: {
          hide: !showMenuBar,
        },

        footer: (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              borderTopStyle: "solid",

              borderTopWidth: 1,

              borderTopColor: (theme) => theme.palette.divider,

              py: 1,
              px: 1.5,
            }}
          >
            <MenuButton
              value="formatting"
              tooltipLabel={showMenuBar ? "Ocultar formato" : "Mostrar formato"}
              size="small"
              onClick={() => setShowMenuBar((currentState) => !currentState)}
              selected={showMenuBar}
              IconComponent={TextFields}
            />

            <MenuButton
              value="formatting"
              tooltipLabel={
                isEditable ? "Bloquear edición" : "Habilitar edición"
              }
              size="small"
              onClick={() => setIsEditable((currentState) => !currentState)}
              selected={!isEditable}
              IconComponent={isEditable ? Lock : LockOpen}
            />
          </Stack>
        ),
      }}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0, // Evita la rigidez intrínseca de flexbox

        // 1. Forzar a los contenedores internos de mui-tiptap a respetar el ancho responsivo
        "& .MuiRichTextField-root, & .MuiInputBase-root, & .MuiOutlinedInput-root":
          {
            width: "100% !important",
            maxWidth: "100% !important",
            minWidth: "0 !important",
            boxSizing: "border-box",
          },

        // 2. Controlar la caja que envuelve directamente el contenido editable
        "& .MuiRichTextField-content": {
          width: "100% !important",
          maxWidth: "100% !important",
          minWidth: "0 !important",
          overflowX: "hidden", // Impide el desborde horizontal del bloque
        },

        // 3. Estilos estrictos para el motor ProseMirror (Tiptap) cuando cargan datos existentes
        "& .ProseMirror": {
          width: "100% !important",
          maxWidth: "100% !important",
          minWidth: "0 !important",
          minHeight: "250px",
          boxSizing: "border-box",
          overflowX: "auto", // Crea scroll interno si hay tablas/código rígidos
          wordBreak: "break-word", // Quiebra palabras/enlaces larguísimos obligatoriamente
          whiteSpace: "pre-wrap !important", // Fuerza a los párrafos HTML a recalcular sus saltos de línea

          // Ajuste para imágenes cargadas previamente en edición
          "& img": {
            maxWidth: "100% !important",
            height: "auto !important",
            display: "block",
          },

          // Ajuste para bloques de código preformateados
          "& pre, & code": {
            maxWidth: "100% !important",
            overflowX: "auto",
          },
        },

        // 4. Ajuste responsivo de la barra de controles (por si se amontona en celulares)
        "& .MuiRichTextEditor-menuControls": {
          flexWrap: "wrap !important",
          gap: "4px",
          padding: { xs: "4px", sm: "8px" },
        },
      }}
    >
      {() => <BubbleMenus />}
    </RichTextEditor>
  );
}
